import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Phone, Plus, ChevronDown, Check, Loader2, Download, X, Settings } from 'lucide-react';
import { usePhoneNumbers } from '@/hooks/usePhoneNumbers';
import { PhoneNumber } from '@/types/phoneNumber';
import { PhoneNumberPurchaser } from './PhoneNumberPurchaser';
import { PhoneNumberConfigModal } from './PhoneNumberConfigModal';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface PhoneNumberSelectorProps {
  value?: string;
  onChange: (phoneNumber: string) => void;
  disabled?: boolean;
  mode?: 'ai-calling' | 'sms-only';
  selectedVapiKey?: string;
}

// Helper function to normalize capabilities format
const normalizeCapabilities = (capabilities: any): string[] => {
  if (!capabilities) return [];
  if (Array.isArray(capabilities)) return capabilities;
  if (typeof capabilities === 'object') {
    return Object.entries(capabilities)
      .filter(([_, enabled]) => enabled)
      .map(([capability]) => capability);
  }
  return [];
};

export function PhoneNumberSelector({ 
  value, 
  onChange, 
  disabled,
  mode = 'ai-calling',
  selectedVapiKey
}: PhoneNumberSelectorProps) {
  const [showPurchaser, setShowPurchaser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedForConfig, setSelectedForConfig] = useState<PhoneNumber | null>(null);
  const [importMode, setImportMode] = useState<'ai-calling' | 'sms-only'>(mode);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [twilioNumbers, setTwilioNumbers] = useState<any[]>([]);
  const [selectedForImport, setSelectedForImport] = useState<Set<string>>(new Set());
  const [vapiApiKey, setVapiApiKey] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const { toast } = useToast();
  const { apiKeys } = useAuth();
  const { 
    phoneNumbers, 
    loading, 
    error, 
    fetchPhoneNumbers, 
    formatPhoneNumber,
    fetchTwilioNumbers,
    importSelectedNumbers
  } = usePhoneNumbers();

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  // Auto-populate VAPI API key from selected key or stored settings
  useEffect(() => {
    if (selectedVapiKey && selectedVapiKey !== vapiApiKey) {
      // Use the selected VAPI key from campaign wizard
      setVapiApiKey(selectedVapiKey);
    } else if (apiKeys?.vapi && !vapiApiKey && !selectedVapiKey) {
      // Fall back to default VAPI key if no specific key selected
      setVapiApiKey(apiKeys.vapi);
    }
  }, [selectedVapiKey, apiKeys?.vapi, vapiApiKey]);

  // Sort phone numbers by creation date (most recent first)
  const sortedNumbers = [...phoneNumbers].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Filter and limit phone numbers based on search state
  const displayNumbers = (() => {
    if (searchValue.trim()) {
      // When searching, show unlimited filtered results
      return sortedNumbers.filter(number => {
        if (!number.phoneNumber) return false;
        
        const search = searchValue.toLowerCase();
        const cleanedSearch = search.replace(/\D/g, '');
        const cleanedPhoneNumber = number.phoneNumber.replace(/\D/g, '');
        
        return (
          (number.friendlyName && number.friendlyName.toLowerCase().includes(search)) ||
          cleanedPhoneNumber.includes(cleanedSearch)
        );
      });
    } else {
      // When not searching, show only 10 most recent
      return sortedNumbers.slice(0, 10);
    }
  })();

  const selectedNumber = phoneNumbers.find(number => number.phoneNumber === value);

  const handleNumberSelect = (phoneNumber: string) => {
    onChange(phoneNumber);
    setOpen(false);
  };

  const handlePurchaseComplete = (newNumber: PhoneNumber) => {
    setShowPurchaser(false);
    onChange(newNumber.phoneNumber);
  };

  const handleConfigureNumber = (phoneNumber: PhoneNumber) => {
    setSelectedForConfig(phoneNumber);
    setShowConfigModal(true);
  };

  const refreshPhoneNumbers = async () => {
    await fetchPhoneNumbers();
  };

  const handleImportClick = async () => {
    try {
      setImportLoading(true);
      const numbers = await fetchTwilioNumbers();
      setTwilioNumbers(numbers);
      setShowImport(true);
    } catch (error) {
      // Error already handled in hook
    } finally {
      setImportLoading(false);
    }
  };

  const handleNumberToggle = (phoneNumber: string) => {
    const newSelected = new Set(selectedForImport);
    if (newSelected.has(phoneNumber)) {
      newSelected.delete(phoneNumber);
    } else {
      newSelected.add(phoneNumber);
    }
    setSelectedForImport(newSelected);
  };

  const handleImportSubmit = async () => {
    if (selectedForImport.size === 0) {
      toast({
        title: 'No Numbers Selected',
        description: 'Please select at least one number to import.',
        variant: 'destructive',
      });
      return;
    }

    if (importMode === 'ai-calling' && !vapiApiKey.trim()) {
      toast({
        title: 'VAPI API Key Required',
        description: 'Please enter your VAPI API key for AI calling setup.',
        variant: 'destructive',
      });
      return;
    }

    setImportLoading(true);
    try {
      const selectedNumbers = Array.from(selectedForImport).map(phoneNumber => 
        twilioNumbers.find(n => n.phoneNumber === phoneNumber)
      ).filter(Boolean);
      
      if (importMode === 'sms-only') {
        // For SMS-only mode, configure numbers without VAPI
        for (const number of selectedNumbers) {
          await handleUseNumberNow(number.phoneNumber);
        }
      } else {
        // For AI calling mode, use VAPI integration
        await importSelectedNumbers(selectedNumbers, vapiApiKey);
      }
      
      toast({
        title: 'Import Successful',
        description: `Successfully imported ${selectedNumbers.length} number(s) for ${importMode === 'sms-only' ? 'SMS' : 'AI calling'}!`,
      });
      
      setShowImport(false);
      setSelectedForImport(new Set());
      setVapiApiKey('');
      setFriendlyName('');
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const handleUseNumberNow = async (phoneNumber: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('configure-twilio-number', {
        body: { 
          phoneNumber,
          friendlyName: friendlyName || `${importMode === 'sms-only' ? 'SMS' : 'AI'} Number ${phoneNumber}`,
        }
      });

      if (error) throw error;

      if (data.success) {
        // Auto-select this number
        onChange(phoneNumber);
        await fetchPhoneNumbers();
        
        toast({
          title: 'Success',
          description: `Number ${phoneNumber} is now ready for ${importMode === 'sms-only' ? 'SMS' : 'AI calling'}!`,
        });
      } else {
        throw new Error(data.error || 'Failed to configure phone number');
      }
    } catch (error) {
      console.error('Configuration failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to configure phone number',
        variant: 'destructive',
      });
    }
  };

  if (loading && phoneNumbers.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading phone numbers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive text-sm">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchPhoneNumbers()}
            className="mt-2"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {mode === 'sms-only' ? 'SMS Phone Number' : 'Phone Number'}
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleImportClick}
            disabled={disabled || importLoading}
            className="text-xs"
          >
            {importLoading ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Download className="w-3 h-3 mr-1" />
            )}
            Import from Twilio
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPurchaser(true)}
            disabled={disabled}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Get New Number
          </Button>
        </div>
      </div>

      {phoneNumbers.length > 0 ? (
        <div className="space-y-3">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between h-auto p-3"
                disabled={disabled}
              >
                {selectedNumber ? (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <div className="flex flex-col text-left">
                      {selectedNumber.friendlyName && (
                        <span className="font-medium">{selectedNumber.friendlyName}</span>
                      )}
                      <span className={selectedNumber.friendlyName ? "text-muted-foreground text-sm" : ""}>
                        {selectedNumber.phoneNumber ? formatPhoneNumber(selectedNumber.phoneNumber) : 'No number'}
                      </span>
                    </div>
                  </div>
                ) : (
                  `Select ${mode === 'sms-only' ? 'SMS' : ''} phone number...`
                )}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search phone numbers..." 
                  className="h-9"
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandEmpty>
                  {searchValue.trim() ? 'No phone numbers match your search.' : 'No phone numbers found.'}
                </CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    {displayNumbers.map((number) => (
                      <CommandItem
                        key={number.id}
                        value={`${number.friendlyName || ''} ${number.phoneNumber}`.toLowerCase()}
                        onSelect={() => handleNumberSelect(number.phoneNumber)}
                        className="flex items-center justify-between py-3"
                      >
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <div>
                            {number.friendlyName && (
                              <div className="font-medium">
                                {number.friendlyName}
                              </div>
                            )}
                            <div className={number.friendlyName ? "text-xs text-muted-foreground" : "font-medium"}>
                              {number.phoneNumber ? formatPhoneNumber(number.phoneNumber) : 'No number'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {normalizeCapabilities(number.capabilities).map((capability) => (
                              <Badge key={capability} variant="secondary" className="text-xs">
                                {capability.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfigureNumber(number);
                            }}
                            className="h-6 w-6 p-0"
                            title="Configure phone number"
                          >
                            <Settings className="h-3 w-3" />
                          </Button>
                          <Check
                            className={`ml-auto h-4 w-4 ${
                              value === number.phoneNumber ? "opacity-100" : "opacity-0"
                            }`}
                          />
                        </div>
                      </CommandItem>
                    ))}
                    {!searchValue.trim() && phoneNumbers.length > 10 && (
                      <div className="px-2 py-1 text-xs text-muted-foreground text-center border-t">
                        Showing 10 most recent numbers. Search to see all {phoneNumbers.length} numbers.
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedNumber && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {selectedNumber.phoneNumber ? formatPhoneNumber(selectedNumber.phoneNumber) : 'No number'}
                    </p>
                    {selectedNumber.friendlyName && (
                      <p className="text-sm text-muted-foreground">
                        {selectedNumber.friendlyName}
                      </p>
                    )}
                    {selectedNumber.locality && selectedNumber.region && (
                      <p className="text-xs text-muted-foreground">
                        {selectedNumber.locality}, {selectedNumber.region}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className="text-xs">
                      {selectedNumber.provider.toUpperCase()}
                    </Badge>
                    <div className="flex gap-1">
                      {normalizeCapabilities(selectedNumber.capabilities).map((capability) => (
                        <Badge key={capability} variant="secondary" className="text-xs">
                          {capability.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">No Phone Numbers Found</CardTitle>
            <CardDescription>
              You don't have any phone numbers imported. Import existing Twilio numbers or purchase a new one to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              type="button"
              onClick={handleImportClick}
              disabled={disabled || importLoading}
              className="w-full"
              variant="default"
            >
              {importLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Import from Twilio
            </Button>
            <Button
              type="button"
              onClick={() => setShowPurchaser(true)}
              disabled={disabled}
              className="w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Get New Number
            </Button>
          </CardContent>
        </Card>
      )}

      <PhoneNumberPurchaser
        isOpen={showPurchaser}
        onClose={() => setShowPurchaser(false)}
        onPurchaseComplete={handlePurchaseComplete}
        provider="twilio"
        onStateRefresh={refreshPhoneNumbers}
        selectedVapiKey={selectedVapiKey}
      />

      <PhoneNumberConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        phoneNumber={selectedForConfig}
        onUpdate={refreshPhoneNumbers}
      />

      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Twilio Phone Numbers</DialogTitle>
            <DialogDescription>
              Select existing Twilio phone numbers to import for {importMode === 'sms-only' ? 'SMS messaging' : 'AI calling with VAPI integration'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Import Mode Selection */}
            <div className="p-4 border rounded-lg bg-muted/50">
              <Label className="text-base font-medium">Import Mode</Label>
              <div className="flex gap-4 mt-2">
                <Button
                  variant={importMode === 'sms-only' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setImportMode('sms-only')}
                >
                  SMS Only
                </Button>
                <Button
                  variant={importMode === 'ai-calling' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setImportMode('ai-calling')}
                >
                  AI Calling + SMS
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {importMode === 'sms-only' 
                  ? 'Import for SMS messaging only. No VAPI integration required.'
                  : 'Import for AI calling with VAPI integration. SMS capability included.'
                }
              </p>
            </div>

            {/* Friendly Name Input */}
            <div className="space-y-2">
              <Label htmlFor="friendlyName">Friendly Name (optional)</Label>
              <Input
                id="friendlyName"
                placeholder={`e.g., ${importMode === 'sms-only' ? 'SMS Support Line' : 'AI Sales Assistant'}`}
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
              />
            </div>

            {/* VAPI API Key - Only show for AI calling mode and when not auto-populated */}
            {importMode === 'ai-calling' && !selectedVapiKey && (
              <div className="space-y-2">
                <Label htmlFor="vapi-key">VAPI API Key (required for AI calling)</Label>
                {!apiKeys?.vapi && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <Settings className="w-4 h-4 text-muted-foreground" />
                    <div className="text-sm">
                      <span className="text-muted-foreground">No VAPI key found in </span>
                      <Button variant="link" className="p-0 h-auto" onClick={() => window.open('/api-settings', '_blank')}>
                        API Settings
                      </Button>
                    </div>
                  </div>
                )}
                <Input
                  id="vapi-key"
                  type="password"
                  placeholder={apiKeys?.vapi ? "Using stored VAPI key..." : "Enter your VAPI API key..."}
                  value={vapiApiKey}
                  onChange={(e) => setVapiApiKey(e.target.value)}
                />
              </div>
            )}

            {/* Show info when VAPI key is auto-populated from campaign wizard */}
            {importMode === 'ai-calling' && selectedVapiKey && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  Using VAPI key from campaign settings: {selectedVapiKey}
                </div>
              </div>
            )}

            {twilioNumbers.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Available Numbers ({twilioNumbers.length})
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    {selectedForImport.size} selected
                  </div>
                </div>
                
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {twilioNumbers.map((number) => (
                    <div
                      key={number.phoneNumber}
                      className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedForImport.has(number.phoneNumber)}
                          onCheckedChange={() => handleNumberToggle(number.phoneNumber)}
                        />
                        <div>
                          <div className="font-medium">
                            {formatPhoneNumber(number.phoneNumber)}
                          </div>
                          {number.friendlyName && (
                            <div className="text-sm text-muted-foreground">
                              {number.friendlyName}
                            </div>
                          )}
                          <div className="flex gap-1 mt-1">
                            {normalizeCapabilities(number.capabilities).map((capability: string) => (
                              <Badge key={capability} variant="secondary" className="text-xs">
                                {capability.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUseNumberNow(number.phoneNumber)}
                      >
                        {importMode === 'sms-only' ? 'Configure for SMS' : 'Use for AI Calls'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No Twilio numbers found</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowImport(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportSubmit}
                disabled={selectedForImport.size === 0 || importLoading}
              >
                {importLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  `Import ${selectedForImport.size} Number${selectedForImport.size === 1 ? '' : 's'}`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}